import { ArrowLeft, BellRing, Calendar, Clock, Globe, Link, Mail, MapPin, Pause, Pencil, Phone, Play, Plus, Store, Trash2, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    getNurturing,
    deleteNurturing,
    toggleNurturingStatus,
    addNurturingEvent
} from "../../interfaces/nurturings";
import { INurturing, NurturingType, NurturingEventType, INurturingEvent } from "../../interfaces/nurturing";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { Select } from "../../components/ui/Select";
import { Input } from "../../components/ui/Input";

const NURTURING_TYPE_LABELS: Record<NurturingType, string> = {
    owner_tracking: "Suivi propriétaire",
    group_tracking: "Suivi d'un groupe",
    franchise_tracking: "Suivi franchise",
    chef_tracking: "Suivi chef de cuisine",
    multisite_tracking: "Suivi multi-sites",
};

type BadgeTone = "neutral" | "accent" | "success" | "warning" | "danger" | "info";

const EVENT_TYPE_LABELS: Record<NurturingEventType, { label: string; icon: string; tone: BadgeTone }> = {
    opening: { label: "Ouverture", icon: "🎉", tone: "success" },
    owner_change: { label: "Changement propriétaire", icon: "👔", tone: "info" },
    director_added: { label: "Ajout directeur", icon: "👥", tone: "neutral" },
    recruitment: { label: "Recrutement", icon: "📢", tone: "info" },
    temporary_closure: { label: "Fermeture temporaire", icon: "⏸️", tone: "warning" },
    permanent_closure: { label: "Fermeture définitive", icon: "🚫", tone: "danger" },
    tech_change: { label: "Changement tech", icon: "💻", tone: "neutral" },
    linkedin_update: { label: "Mise à jour LinkedIn", icon: "💼", tone: "neutral" },
    google_maps_update: { label: "Publication Google Maps", icon: "📍", tone: "neutral" },
    inpi_update: { label: "Mise à jour INPI", icon: "📋", tone: "neutral" },
    website_change: { label: "Changement site web", icon: "🌐", tone: "neutral" },
    social_media_activity: { label: "Activité réseaux sociaux", icon: "📱", tone: "neutral" },
    review_spike: { label: "Pic d'avis", icon: "⭐", tone: "accent" },
    rating_change: { label: "Changement de note", icon: "📊", tone: "info" },
    menu_update: { label: "Mise à jour menu", icon: "🍽️", tone: "neutral" },
    other: { label: "Autre", icon: "ℹ️", tone: "neutral" },
};

export default function NurturingDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [nurturing, setNurturing] = useState<INurturing | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showAddEventModal, setShowAddEventModal] = useState(false);

    useEffect(() => {
        if (id) {
            fetchNurturing();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchNurturing = async () => {
        try {
            setLoading(true);
            const data = await getNurturing(id!);
            setNurturing(data);
        } catch (err) {
            console.error("Failed to fetch nurturing:", err);
            setError("Échec du chargement du nurturing");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!nurturing) return;

        if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${nurturing.entityName}" ?`)) {
            return;
        }

        try {
            await deleteNurturing(nurturing._id);
            navigate("/nurturings");
        } catch (err) {
            console.error("Failed to delete nurturing:", err);
            alert("Échec de la suppression");
        }
    };

    const handleToggleStatus = async () => {
        if (!nurturing) return;

        try {
            const result = await toggleNurturingStatus(nurturing._id);
            setNurturing(result.nurturing);
        } catch (err) {
            console.error("Failed to toggle status:", err);
            alert("Échec de la mise à jour du statut");
        }
    };

    const formatDateTime = (dateString?: string | Date) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const getStatusTone = (status: string): BadgeTone => {
        if (status === "active") return "success";
        if (status === "paused") return "warning";
        return "neutral";
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-canvas font-sans flex items-center justify-center">
                <div className="text-sm text-ink-muted">Chargement...</div>
            </div>
        );
    }

    if (error || !nurturing) {
        return (
            <div className="min-h-screen bg-canvas font-sans flex flex-col items-center justify-center">
                <div className="text-sm text-danger mb-4">{error || "Nurturing non trouvé"}</div>
                <Button onClick={() => navigate("/nurturings")}>
                    Retour
                </Button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-canvas font-sans">
            <div className="mx-auto max-w-7xl px-4 md:px-8 py-6">
                {/* Back Button */}
                <button
                    onClick={() => navigate("/nurturings")}
                    className="flex items-center gap-2 text-ink-muted hover:text-ink mb-6 text-sm transition-colors duration-150"
                >
                    <ArrowLeft className="h-4 w-4" />
                    <span>Retour</span>
                </button>

                {/* Header */}
                <div className="rounded-2xl border border-border bg-surface p-6 mb-6">
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                            <div className="mb-3">
                                <h1 className="font-sans text-2xl font-semibold tracking-tight text-ink">
                                    {nurturing.displayName || nurturing.entityName}
                                </h1>
                                {nurturing.displayName && (
                                    <p className="text-sm text-ink-muted mt-1">{nurturing.entityName}</p>
                                )}
                            </div>

                            <div className="flex flex-wrap items-center gap-2 mb-3">
                                <Badge tone={getStatusTone(nurturing.status)}>
                                    {nurturing.status === 'active' ? 'Actif' : nurturing.status === 'paused' ? 'Pause' : 'Archivé'}
                                </Badge>

                                <Badge tone="neutral">
                                    {NURTURING_TYPE_LABELS[nurturing.type]}
                                </Badge>

                                {nurturing.priority && nurturing.priority !== 'medium' && (
                                    <Badge tone={nurturing.priority === 'high' ? 'danger' : 'neutral'}>
                                        {nurturing.priority === 'high' ? 'Haute' : 'Faible'}
                                    </Badge>
                                )}

                                {nurturing.alertCount > 0 && (
                                    <Badge tone="danger">
                                        <BellRing className="h-3 w-3" />
                                        {nurturing.alertCount}
                                    </Badge>
                                )}
                            </div>

                            {nurturing.tags && nurturing.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                    {nurturing.tags.map((tag, index) => (
                                        <span key={index} className="px-2 py-0.5 bg-surface-sunken text-ink-muted rounded-full text-xs">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="flex gap-2">
                            <Button variant="secondary" size="sm" onClick={handleToggleStatus}>
                                {nurturing.status === 'active' ? (
                                    <Pause className="h-4 w-4" />
                                ) : (
                                    <Play className="h-4 w-4" />
                                )}
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => navigate(`/nurturings/${nurturing._id}/edit`)}>
                                <Pencil className="h-4 w-4" />
                            </Button>
                            <button
                                onClick={handleDelete}
                                className="inline-flex items-center justify-center h-9 px-3 rounded-lg border border-border-strong text-danger text-sm hover:bg-danger-subtle transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Info */}
                    <div className="lg:col-span-1 space-y-4">
                        {/* Contact Info */}
                        <div className="rounded-2xl border border-border bg-surface p-6">
                            <h2 className="text-sm font-semibold text-ink mb-4">Informations</h2>

                            <div className="space-y-3">
                                {nurturing.enrichmentData?.city && (
                                    <div className="flex items-start gap-3">
                                        <MapPin className="h-5 w-5 text-ink-faint mt-0.5" />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-ink-muted">Localisation</div>
                                            <div className="text-sm text-ink">
                                                {nurturing.enrichmentData.address && `${nurturing.enrichmentData.address}, `}
                                                {nurturing.enrichmentData.city}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {nurturing.phone && (
                                    <div className="flex items-start gap-3">
                                        <Phone className="h-5 w-5 text-ink-faint mt-0.5" />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-ink-muted">Téléphone</div>
                                            <a href={`tel:${nurturing.phone}`} className="text-sm text-ink hover:underline">
                                                {nurturing.phone}
                                            </a>
                                        </div>
                                    </div>
                                )}

                                {nurturing.email && (
                                    <div className="flex items-start gap-3">
                                        <Mail className="h-5 w-5 text-ink-faint mt-0.5" />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-ink-muted">Email</div>
                                            <a href={`mailto:${nurturing.email}`} className="text-sm text-ink hover:underline">
                                                {nurturing.email}
                                            </a>
                                        </div>
                                    </div>
                                )}

                                {nurturing.siren && (
                                    <div className="flex items-start gap-3">
                                        <Store className="h-5 w-5 text-ink-faint mt-0.5" />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-ink-muted">SIREN</div>
                                            <div className="text-sm text-ink font-mono">{nurturing.siren}</div>
                                        </div>
                                    </div>
                                )}

                                {nurturing.lastCheckAt && (
                                    <div className="flex items-start gap-3">
                                        <Clock className="h-5 w-5 text-ink-faint mt-0.5" />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-ink-muted">Dernier check</div>
                                            <div className="text-sm text-ink">{formatDateTime(nurturing.lastCheckAt)}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Links */}
                        <div className="rounded-2xl border border-border bg-surface p-6">
                            <h2 className="font-sans text-sm font-semibold text-ink mb-4">Liens</h2>

                            <div className="space-y-1">
                                {nurturing.googleMapsUrl && (
                                    <a
                                        href={nurturing.googleMapsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 px-3 py-2 text-sm text-ink-muted hover:bg-surface-sunken hover:text-ink rounded-lg transition-colors duration-150"
                                    >
                                        <MapPin className="h-4 w-4" />
                                        <span>Google Maps</span>
                                        <Link className="h-3 w-3 ml-auto" />
                                    </a>
                                )}
                                {nurturing.websiteUrl && (
                                    <a
                                        href={nurturing.websiteUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 px-3 py-2 text-sm text-ink-muted hover:bg-surface-sunken hover:text-ink rounded-lg transition-colors duration-150"
                                    >
                                        <Globe className="h-4 w-4" />
                                        <span>Site web</span>
                                        <Link className="h-3 w-3 ml-auto" />
                                    </a>
                                )}
                                {nurturing.linkedinUrl && (
                                    <a
                                        href={nurturing.linkedinUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 px-3 py-2 text-sm text-ink-muted hover:bg-surface-sunken hover:text-ink rounded-lg transition-colors duration-150"
                                    >
                                        <Link className="h-4 w-4" />
                                        <span>LinkedIn</span>
                                        <Link className="h-3 w-3 ml-auto" />
                                    </a>
                                )}
                                {nurturing.instagramUrl && (
                                    <a
                                        href={nurturing.instagramUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 px-3 py-2 text-sm text-ink-muted hover:bg-surface-sunken hover:text-ink rounded-lg transition-colors duration-150"
                                    >
                                        <Link className="h-4 w-4" />
                                        <span>Instagram</span>
                                        <Link className="h-3 w-3 ml-auto" />
                                    </a>
                                )}
                                {nurturing.tiktokUrl && (
                                    <a
                                        href={nurturing.tiktokUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 px-3 py-2 text-sm text-ink-muted hover:bg-surface-sunken hover:text-ink rounded-lg transition-colors duration-150"
                                    >
                                        <Link className="h-4 w-4" />
                                        <span>TikTok</span>
                                        <Link className="h-3 w-3 ml-auto" />
                                    </a>
                                )}
                            </div>
                        </div>

                        {/* Notes */}
                        {nurturing.notes && (
                            <div className="rounded-2xl border border-border bg-surface p-6">
                                <h2 className="font-sans text-sm font-semibold text-ink mb-3">Notes</h2>
                                <p className="text-sm text-ink-muted whitespace-pre-wrap">{nurturing.notes}</p>
                            </div>
                        )}
                    </div>

                    {/* Right Column - Timeline */}
                    <div className="lg:col-span-2">
                        <div className="rounded-2xl border border-border bg-surface p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="font-sans text-sm font-semibold text-ink">Timeline des événements</h2>
                                <Button size="sm" onClick={() => setShowAddEventModal(true)}>
                                    <Plus className="h-4 w-4" />
                                    <span>Ajouter un événement</span>
                                </Button>
                            </div>

                            {/* Timeline */}
                            {nurturing.events && nurturing.events.length > 0 ? (
                                <div className="relative">
                                    {/* Vertical line */}
                                    <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border"></div>

                                    <div className="space-y-6">
                                        {[...nurturing.events]
                                            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                                            .map((event, index) => {
                                                const eventStyle = EVENT_TYPE_LABELS[event.type] || EVENT_TYPE_LABELS.other;
                                                return (
                                                    <TimelineEvent
                                                        key={index}
                                                        event={event}
                                                        eventStyle={eventStyle}
                                                    />
                                                );
                                            })}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <Calendar className="h-12 w-12 text-ink-faint mx-auto mb-3" />
                                    <p className="text-ink-muted text-sm">Aucun événement détecté pour le moment</p>
                                    <button
                                        onClick={() => setShowAddEventModal(true)}
                                        className="mt-4 text-ink hover:underline text-sm font-medium"
                                    >
                                        Ajouter le premier événement
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Add Event Modal */}
                {showAddEventModal && (
                    <AddEventModal
                        nurturingId={nurturing._id}
                        onClose={() => setShowAddEventModal(false)}
                        onSuccess={() => {
                            setShowAddEventModal(false);
                            fetchNurturing();
                        }}
                    />
                )}
            </div>
        </div>
    );
}

// Timeline Event Component
function TimelineEvent({
    event,
    eventStyle
}: {
    event: INurturingEvent;
    eventStyle: { label: string; icon: string; tone: BadgeTone }
}) {
    const formatDateTime = (dateString: string | Date) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    return (
        <div className="relative pl-14">
            {/* Icon */}
            <div className="absolute left-0 w-12 h-12 rounded-full bg-surface border-2 border-border flex items-center justify-center text-xl">
                {eventStyle.icon}
            </div>

            {/* Content */}
            <div className="bg-surface-sunken rounded-lg p-4">
                <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                            <Badge tone={eventStyle.tone}>
                                {eventStyle.label}
                            </Badge>
                            <span className="text-xs text-ink-muted">{formatDateTime(event.date)}</span>
                        </div>
                        <h3 className="font-sans font-semibold text-ink mb-1">{event.title}</h3>
                    </div>
                </div>

                <p className="text-sm text-ink-muted mb-3">{event.description}</p>

                {event.sourceUrl && (
                    <a
                        href={event.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-ink hover:underline"
                    >
                        <Link className="h-3 w-3" />
                        <span>Voir la source</span>
                    </a>
                )}
            </div>
        </div>
    );
}

// Add Event Modal Component
function AddEventModal({
    nurturingId,
    onClose,
    onSuccess
}: {
    nurturingId: string;
    onClose: () => void;
    onSuccess: () => void
}) {
    const [formData, setFormData] = useState<Partial<INurturingEvent>>({
        type: 'other',
        date: new Date().toISOString().slice(0, 16),
        title: '',
        description: '',
        sourceUrl: '',
    });
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.title || !formData.description) {
            alert("Veuillez remplir tous les champs obligatoires");
            return;
        }

        try {
            setLoading(true);
            await addNurturingEvent(nurturingId, formData);
            onSuccess();
        } catch (error) {
            console.error("Failed to add event:", error);
            alert("Échec de l'ajout de l'événement");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50 p-4">
            <div className="rounded-2xl bg-surface shadow-float max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-surface border-b border-border px-6 py-4 flex items-center justify-between rounded-t-2xl">
                    <h2 className="font-sans text-xl font-semibold text-ink">Ajouter un événement</h2>
                    <button
                        onClick={onClose}
                        className="text-ink-faint hover:text-ink transition-colors duration-150"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-ink mb-1.5">
                            Type d'événement <span className="text-danger">*</span>
                        </label>
                        <Select
                            fullWidth
                            value={formData.type}
                            onChange={(v) => setFormData({ ...formData, type: v as NurturingEventType })}
                            options={Object.entries(EVENT_TYPE_LABELS).map(([value, { label }]) => ({ value, label }))}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-ink mb-1.5">
                            Date et heure <span className="text-danger">*</span>
                        </label>
                        <Input
                            type="datetime-local"
                            value={formData.date as string}
                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-ink mb-1.5">
                            Titre <span className="text-danger">*</span>
                        </label>
                        <Input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            placeholder="Ex: Nouveau chef embauché"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-ink mb-1.5">
                            Description <span className="text-danger">*</span>
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Détails de l'événement..."
                            rows={4}
                            className="w-full rounded-lg border border-border bg-surface px-3 py-2 font-sans text-sm text-ink placeholder:text-ink-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-ink mb-1.5">
                            URL source (optionnel)
                        </label>
                        <Input
                            type="url"
                            value={formData.sourceUrl}
                            onChange={(e) => setFormData({ ...formData, sourceUrl: e.target.value })}
                            placeholder="https://..."
                        />
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-border">
                        <Button type="button" variant="secondary" onClick={onClose}>
                            Annuler
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading ? "Ajout en cours..." : "Ajouter l'événement"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
